import React, { useState } from "react";
import {
  Alert, Box, Button, Card, Chip, Container, Grid, Snackbar, Stack, TextField, Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";

const REDGRAD = "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 50%, #E64A19 100%)";
const AMBERGRAD = "linear-gradient(135deg, #FF8F00 0%, #FFB300 50%, #FFC107 100%)";

const INFO = [
  { icon: <LocationOnRoundedIcon />, title: "Visit Us", lines: ["123 Pizza Street", "Foodie District, NY 10001"] },
  { icon: <PhoneRoundedIcon />, title: "Call Us", lines: ["+1 (555) 123-4567", "Mon - Sun, 10am - 11pm"] },
  { icon: <EmailRoundedIcon />, title: "Email Us", lines: ["hello@pizzatown.com", "support@pizzatown.com"] },
  { icon: <AccessTimeRoundedIcon />, title: "Open Hours", lines: ["Mon - Fri: 10am - 11pm", "Sat - Sun: 11am - 12am"] },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setSnackbar({ open: true, message: "Please fill in all required fields.", severity: "error" });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSnackbar({ open: true, message: "Message sent! We'll get back to you soon. 🍕", severity: "success" });
      setForm({ name: "", email: "", subject: "", message: "" });
    }, 1200);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      background: "#FFF8F0",
      "& fieldset": { borderColor: "rgba(183,28,28,0.15)" },
      "&:hover fieldset": { borderColor: "rgba(183,28,28,0.4)" },
      "&.Mui-focused fieldset": { borderColor: "#B71C1C", borderWidth: 2 },
    },
    "& label.Mui-focused": { color: "#B71C1C" },
  };

  return (
    <Box sx={{ background: "#FFF8F0", overflow: "hidden", py: { xs: 6, md: 10 } }}>
      {/* HERO */}
      <Container maxWidth="md" sx={{ textAlign: "center", mb: 8, position: "relative" }}>
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
          style={{
            position: "absolute", top: -40, left: "10%",
            width: 200, height: 200, borderRadius: "50%",
            background: AMBERGRAD, filter: "blur(80px)", opacity: 0.3, zIndex: 0,
          }}
        />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} style={{ position: "relative" }}>
          <Chip label="GET IN TOUCH" sx={{ mb: 3, fontWeight: 800, letterSpacing: 2, background: "rgba(183,28,28,0.1)", color: "#B71C1C" }} />
          <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 64 }, lineHeight: 1.05, mb: 2 }}>
            Let's{" "}
            <Box component="span" sx={{ background: REDGRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              talk food
            </Box>
            {" "}🍕
          </Typography>
          <Typography sx={{ fontSize: { xs: 16, md: 19 }, color: "#6b4a3a", maxWidth: 580, mx: "auto" }}>
            Questions, feedback, catering inquiries, or just want to say hi? We'd love to hear from you.
          </Typography>
        </motion.div>
      </Container>

      {/* INFO CARDS */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={3}>
          {INFO.map((info, i) => (
            <Grid item xs={12} sm={6} md={3} key={info.title}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <Card sx={{ p: 3, height: "100%", borderRadius: 4, textAlign: "center", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", border: "1px solid rgba(183,28,28,0.08)" }}>
                  <Box
                    sx={{
                      width: 56, height: 56, mx: "auto", mb: 2, borderRadius: "50%",
                      background: REDGRAD, color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      "& svg": { fontSize: 28 },
                      boxShadow: "0 10px 24px rgba(183,28,28,0.3)",
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 1 }}>{info.title}</Typography>
                  {info.lines.map((l) => (
                    <Typography key={l} sx={{ fontSize: 14, color: "#6b4a3a", lineHeight: 1.6 }}>{l}</Typography>
                  ))}
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FORM + SIDE */}
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 5, boxShadow: "0 20px 50px rgba(0,0,0,0.08)" }}>
                <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 34 }, mb: 1 }}>Send us a message</Typography>
                <Typography sx={{ color: "#6b4a3a", mb: 4 }}>We typically reply within a few hours.</Typography>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Your Name *" name="name" value={form.name} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email *" name="email" type="email" value={form.email} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Subject" name="subject" value={form.subject} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={5} label="Your Message *" name="message" value={form.message} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        size="large"
                        disabled={loading}
                        endIcon={!loading && <SendRoundedIcon />}
                        sx={{
                          py: 1.7, fontSize: 16, fontWeight: 800, color: "#fff",
                          background: REDGRAD,
                          boxShadow: "0 14px 34px rgba(183,28,28,0.4)",
                          "&:hover": { boxShadow: "0 20px 44px rgba(183,28,28,0.55)", transform: "translateY(-2px)" },
                          transition: "all 0.3s",
                        }}
                      >
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={5}>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: 5,
                  background: REDGRAD,
                  color: "#fff",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(183,28,28,0.35)",
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  style={{
                    position: "absolute", top: -80, right: -80,
                    width: 240, height: 240, borderRadius: "50%",
                    background: "rgba(255,179,0,0.2)",
                  }}
                />
                <Box sx={{ position: "relative" }}>
                  <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 32 }, mb: 2, color: "#fff" }}>
                    Visit our kitchen 🔥
                  </Typography>
                  <Typography sx={{ fontSize: 16, opacity: 0.95, mb: 4, lineHeight: 1.7 }}>
                    Watch our wood-fired ovens in action. Smell the basil. Taste the magic. Drop by anytime — we love showing off.
                  </Typography>

                  <Box sx={{ p: 2.5, borderRadius: 3, background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)", mb: 3 }}>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>📍 Flagship Location</Typography>
                    <Typography sx={{ fontSize: 14, opacity: 0.9 }}>123 Pizza Street, Foodie District, NY 10001</Typography>
                  </Box>

                  <Typography sx={{ fontWeight: 800, mb: 2 }}>Follow the foodie life</Typography>
                  <Stack direction="row" spacing={1.5}>
                    {[InstagramIcon, FacebookIcon, TwitterIcon].map((Icon, i) => (
                      <motion.div key={i} whileHover={{ scale: 1.15, y: -3 }} whileTap={{ scale: 0.9 }}>
                        <Box
                          sx={{
                            width: 44, height: 44, borderRadius: "50%",
                            background: "rgba(255,255,255,0.15)",
                            backdropFilter: "blur(10px)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer",
                            border: "1px solid rgba(255,255,255,0.2)",
                            "&:hover": { background: "#FFB300" },
                            transition: "all 0.3s",
                          }}
                        >
                          <Icon sx={{ color: "#fff" }} />
                        </Box>
                      </motion.div>
                    ))}
                  </Stack>
                </Box>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* MAP placeholder */}
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Card
            sx={{
              borderRadius: 5,
              overflow: "hidden",
              height: { xs: 280, md: 400 },
              background: "linear-gradient(135deg, #FFF1E0 0%, #FFE0B2 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexDirection: "column",
              boxShadow: "0 20px 50px rgba(0,0,0,0.08)",
              position: "relative",
            }}
          >
            <Box sx={{ fontSize: { xs: 80, md: 120 }, mb: 1 }}>🗺️</Box>
            <Typography variant="h3" sx={{ fontSize: { xs: 22, md: 28 }, color: "#B71C1C" }}>
              Find us on the map
            </Typography>
            <Typography sx={{ color: "#6b4a3a", mt: 1 }}>123 Pizza Street, Foodie District</Typography>
          </Card>
        </motion.div>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 3, fontWeight: 600, boxShadow: "0 12px 30px rgba(0,0,0,0.2)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
